import type { SceneAsset } from "../scene/model";
import { imageMediaType } from "../native/image-formats";
import { PixelBudget } from "./pixel-budget";
import { ImageLevels } from "./image-levels";
import { NativeRenderWorker } from "./native-worker-client";
import { DisplayWork } from "./display-work";
export class ImageStore {
  readonly displayWork = new DisplayWork();
  readonly pixels = new PixelBudget();
  readonly worker = new NativeRenderWorker();
  private full = new WeakSet<HTMLImageElement>();
  readonly levels = new ImageLevels(
    32 * 1024 * 1024,
    this.worker,
    () => this.invalidate(),
    this.pixels,
  );
  revision = 0;
  constructor(private changed = () => {}) {}
  invalidate() {
    this.revision++;
    this.changed();
  }
  private images = new Map<string, HTMLImageElement>();
  private pending = new Map<string, Promise<void>>();
  private epoch = 0;
  get(source: string) {
    return this.images.get(source);
  }
  display(source: string, width: number, height: number) {
    const image = this.get(source);
    return image ? this.levels.get(image, width, height) : undefined;
  }
  /** Project opening requests small display copies; precise consumers call prepare(). */
  async prepareDisplay(assets: readonly SceneAsset[]) {
    return this.prepare(assets, true);
  }
  async prepare(assets: readonly SceneAsset[], displayOnly = false) {
    const queue = assets.filter((a) => a.kind === "image");
    let index = 0;
    const worker = async () => {
      while (index < queue.length) {
        const asset = queue[index++]!;
        const existing = this.pending.get(asset.source);
        if (existing) {
          await existing;
          if (this.images.has(asset.source) && displayOnly) continue;
        }
        const epoch = this.epoch;
        const load = async () => {
          let image = this.images.get(asset.source);
          if (image) {
            if (!displayOnly && !this.full.has(image)) {
              await image.decode();
              this.full.add(image);
            }
            return;
          }
          image = new Image();
          image.decoding = "async";
          try {
            if (displayOnly && typeof createImageBitmap !== "undefined") {
              // onload obtains intrinsic dimensions without awaiting a full-resolution decode.
              // Browser-internal decode/cache policy remains browser-owned.
              await new Promise<void>((resolve, reject) => {
                image!.onload = () => resolve();
                image!.onerror = reject;
                image!.src = asset.source;
              });
            } else {
              image.src = asset.source;
              await image.decode();
              this.full.add(image);
            }
          } catch {
            throw new Error(
              "Не удалось прочитать изображение. Выберите PNG, JPEG, WebP, SVG или GIF.",
            );
          }
          if (!image.naturalWidth || !image.naturalHeight)
            throw new Error("У изображения отсутствует размер.");
          if (image.naturalWidth * image.naturalHeight > 32_000_000)
            throw new Error(
              "Для этого прототипа выберите изображение до 32 мегапикселей.",
            );
          if (
            displayOnly &&
            typeof createImageBitmap !== "undefined" &&
            Math.max(image.naturalWidth, image.naturalHeight) > 512
          ) {
            const ratio =
              256 / Math.max(image.naturalWidth, image.naturalHeight);
            const size = {
              width: Math.max(1, Math.ceil(image.naturalWidth * ratio)),
              height: Math.max(1, Math.ceil(image.naturalHeight * ratio)),
            };
            // Decode the encoded source directly at preview size where the browser supports it.
            // Full-resolution pixels are requested only by display refinement / exact consumers.
            try {
              const response = await fetch(asset.source, {
                credentials: "same-origin",
              });
              if (!response.ok)
                throw new Error("Не удалось загрузить изображение проекта.");
              const bitmap = await createImageBitmap(await response.blob(), {
                resizeWidth: size.width,
                resizeHeight: size.height,
                resizeQuality: "high",
                premultiplyAlpha: "none",
              });
              if (epoch === this.epoch) this.levels.seed(image, bitmap);
              else bitmap.close();
            } catch {
              // CORS / decoder support may differ from HTMLImageElement. Preserve
              // the existing exact source path instead of rejecting a valid project.
              await image.decode();
              this.full.add(image);
            }
          }
          image.onload = image.onerror = null;
          if (epoch === this.epoch) {
            this.images.set(asset.source, image);
            this.revision++;
          }
        };
        const promise = load();
        this.pending.set(asset.source, promise);
        try {
          await promise;
        } finally {
          if (this.pending.get(asset.source) === promise)
            this.pending.delete(asset.source);
        }
      }
    };
    await Promise.all([worker(), worker()]);
  }
  stats() {
    const all = [...this.images.values()];
    return {
      sources: all.length,
      explicitlyDecoded: all.filter((image) => this.full.has(image)).length,
      fullSizeRgbaEquivalent: all.reduce(
        (sum, image) => sum + image.naturalWidth * image.naturalHeight * 4,
        0,
      ),
    };
  }
  /** Called only after a document replacement commits; its prior history is already discarded. */
  retain(assets: readonly SceneAsset[]) {
    this.epoch++;
    this.displayWork.reset();
    this.worker.reset();
    this.pixels.clear(["displayRasters", "hitRasters"]);
    const sources = new Set(assets.map((a) => a.source));
    for (const source of this.images.keys())
      if (!sources.has(source)) this.images.delete(source);
    this.levels.retain(new Set(this.images.values()));
  }
}
export async function readImageFile(file: File): Promise<string> {
  const type = imageMediaType(file.type, file.name);
  if (!type) throw new Error("Поддерживаются PNG, JPEG, WebP, SVG и GIF.");
  if (file.size > 24 * 1024 * 1024)
    throw new Error("Выберите изображение до 24 МБ.");
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Не удалось прочитать файл."));
    reader.readAsDataURL(file.slice(0, file.size, type));
  });
}
